import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/photos`).then((r) => {
      if (alive) setPhotos(r.data.photos || []);
    });
    return () => { alive = false; };
  }, []);

  const hero = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-exterior-property"),
    [photos],
  );
  const secondary = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-property-view"),
    [photos],
  );

  const canonical = "https://westbridgehotels.com/about";
  useSeo({
    title: "About Westbridge Inn & Suites — Centerville, Iowa Hotel Since Motel 60",
    description:
      "The largest and newest motel in southern Iowa, independently operated in Centerville. Two blocks from the historic square and seven miles from Rathbun Lake.",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: [buildBreadcrumbLd([{ label: "About", to: "/about" }])],
  });

  return (
    <main id="main" data-testid="about-page">
      <PageHero
        eyebrow="About us"
        title="The largest and newest motel in southern Iowa."
        intro="Westbridge Inn & Suites sits two blocks off the Centerville historic square, seven miles from Rathbun Lake, and about an hour and a half from Des Moines. Independently owned and operated — the same family who signs the paychecks answers the phone."
        photo={hero}
        breadcrumbs={[{ label: "About", to: "/about" }]}
        testId="about-hero"
      />

      {/* Our story */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7 max-w-xl">
            <p className="eyebrow mb-4">Our story</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              From <span className="italic text-brass">Motel 60</span> to Westbridge Inn &amp; Suites.
            </h2>
            <div className="mt-8 space-y-5 text-ink/85 text-lg leading-relaxed">
              <p>
                For years, southern Iowa travelers knew this property as Motel 60 — a reliable stop between Des Moines and Kansas City, and a base for weekend trips to Rathbun Lake. The property was rebuilt and rebranded as Westbridge Inn &amp; Suites: 52 well-kept rooms across an interior corridor and an exterior wing, an indoor pool with an ADA lift, and a meeting room set up for reunions, weddings, and small business events.
              </p>
              <p>
                What didn't change: real people at the front desk, honest rates without resort fees, and a lot with room for a big truck. What changed: everything else. It's the largest and newest lodging in southern Iowa.
              </p>
            </div>
          </div>
          <aside className="md:col-span-5 bg-cream-100 border border-tan p-6 md:p-8">
            <p className="eyebrow mb-4">Quick facts</p>
            <dl className="divide-y divide-tan-200 text-sm">
              <FactRow label="Rooms" value="52 total · 22 interior · 30 exterior" />
              <FactRow label="Address" value="117 N 18th St, Centerville, IA 52544" />
              <FactRow label="Distance to Rathbun Lake" value="~7 miles" />
              <FactRow label="Historic square" value="A short walk (two blocks)" />
              <FactRow label="Formerly known as" value="Motel 60" />
              <FactRow label="Ownership" value="Independently owned & operated" />
              <FactRow label="Front desk" value="24 hours a day, 7 days a week" />
            </dl>
          </aside>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream-100 py-16 md:py-24 paper-grain">
        <div className="container-app relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="block w-full aspect-[4/3] overflow-hidden bg-forest-800">
              {secondary && (
                <Picture
                  photo={secondary}
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  sizes="(min-width: 768px) 45vw, 100vw"
                />
              )}
            </div>
          </div>
          <div className="md:col-span-6 max-w-xl">
            <p className="eyebrow mb-4">What we care about</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
              Three quiet promises.
            </h2>
            <ul className="mt-8 space-y-5 text-ink/85 text-lg leading-relaxed">
              <li className="flex gap-3">
                <span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" />
                <span><strong className="text-forest-900">Clean rooms.</strong> This is the biggest reason guests come back and the biggest reason guests leave. We take it seriously.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" />
                <span><strong className="text-forest-900">Honest facts.</strong> If the pool is down for maintenance, we tell you before you pay. If a room is exterior-access, we say so on the page. No resort fees, no surprises at check-out.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" />
                <span><strong className="text-forest-900">Real people.</strong> A human at the front desk 24 hours a day. Not a chatbot, not a phone tree — a real person who can hold your key or give you directions to Sharon Bluffs.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Local pride */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app max-w-3xl">
          <p className="eyebrow mb-4">In southern Iowa</p>
          <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-[1.05]">
            Two blocks off the square. Seven miles from the lake.
          </h2>
          <div className="mt-8 space-y-5 text-ink/85 text-lg leading-relaxed">
            <p>
              Centerville was founded in 1846 and named for William Tandy Senter — a Tennessee politician the town admired — before an 1855 incorporation record accidentally recorded the name as "Centerville." The name stuck. So did the two-block town square, which is still the anchor of local life today.
            </p>
            <p>
              We're proud to be a small part of that. If it's your first trip to Centerville, stop by the front desk on the way in — we'll point you to the courthouse, the Coal Mining Museum, Rathbun Lake, and where to eat when nothing on Google is currently open.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-ghost-dark text-sm">Find us on the map →</Link>
            <Link to="/rooms" className="btn-ghost-dark text-sm">See our rooms →</Link>
          </div>
        </div>
      </section>

      <CtaBlock />
    </main>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3">
      <dt className="text-xs uppercase tracking-[0.14em] text-ink/60 col-span-1">{label}</dt>
      <dd className="col-span-2 text-forest-900">{value}</dd>
    </div>
  );
}
